/* cf-gate-panel-billing.js — Click Fencing
 *
 * A panel the same width as the gate is never billed.
 *
 * Measured on the published theme, 2026-08-26:
 *
 *   PerfMesh 10350 / 2000 sheets, 1000mm gate
 *     drawn  5 panels (2000 x4 + 1000) and 7 posts
 *     billed 4 panel kits and 6 posts        -> one 2m kit ($467.56) + one post ($50.16)
 *                                               = $517.72 never charged
 *   PerfMesh 10400  identical.
 *   Flat top 6990 / 2400, 970mm gate
 *     drawn  3 panels (2400, 2400, 970)      billed 2 panels
 *
 * 10450 (a 1100 panel) and 11000 (a 700 panel) are both correct, and so is every
 * ungated section. The defect fires ONLY when a drawn panel's width happens to equal
 * the gate footprint. This is the $517.72 discrepancy first seen on 2026-08-21 and
 * carried as "the 10400 defect" ever since; it was never about 10400.
 *
 * The cause
 * ---------
 * calculatePanelCosts builds its panel list from one of three sources and, in each,
 * strips the gate with a test on WIDTH:
 *
 *     .filter(p => !p.is_gate && !p.is_hinge && !(gateLengthMm && p.sheet === gateLengthMm))
 *
 * That test cannot tell the gate from a panel that merely measures the same. And it is
 * not even needed:
 *
 *   - the cut plan (calculateJobCutPlan) does NOT contain the gate at all -- verified:
 *     at 10350 it returns exactly the five panels, and every entry has is_gate
 *     undefined. So there the width test has nothing legitimate to remove and can only
 *     ever delete a real panel.
 *   - renderedPanels DOES contain the gate, but types it: { width: 1000, type: 'gate',
 *     isGate: true }. The type test already removes it.
 *
 * So on both real paths the width test is pure damage.
 *
 * The fix
 * -------
 * Set section.gateLength to -1 for the duration of the original call. parseInt gives
 * -1, which is truthy, so the guard stays live but `p.sheet === -1` never matches and
 * no real panel is lost. The gate is still removed -- by is_gate on the cut plan path
 * (where it was never present) and by type === 'gate' on the rendered path.
 *
 * The post count then corrects itself: postCount is panelWidths.length + 1 + gate +
 * rake, so restoring the fifth panel restores the seventh post. Nothing about posts is
 * special-cased here.
 *
 * Why the plan is computed first
 * ------------------------------
 * calculateJobCutPlan reads section.gateLength to decide the layout -- at 10350 it
 * reserves the gate's 1000mm. Running it under the sentinel would plan a DIFFERENT
 * fence. So the plan is computed with the true gate length, cached, and served to the
 * original from the cache while the sentinel is in force. The sentinel changes which
 * panels get billed, never which panels get built.
 *
 * When it stands down
 * -------------------
 * Only the two typed sources are safe. If neither a valid cut plan nor renderedPanels
 * is available, calculatePanelCosts falls back to calculation.panelWidths -- a bare
 * list of numbers with no type information, where the width test may be the only thing
 * removing the gate. There the sentinel could turn an under-bill into an over-bill, so
 * this does nothing at all and leaves the quote exactly as the calculator built it.
 *
 * Applies to all three aluminium calculators; they share this code shape. Glass builds
 * its layout elsewhere and is untouched.
 */
(function () {
  'use strict';
  if (window.__cfGatePanelBillingInstalled) return;
  window.__cfGatePanelBillingInstalled = 1;

  var LOG = '[cf-gate-panel-billing]';
  var SENTINEL = -1;

  function isArr(x) { return Object.prototype.toString.call(x) === '[object Array]'; }

  function sectionKey(gc, section) {
    try {
      for (var k in gc.state.sections) {
        if (gc.state.sections[k] === section) return k;
      }
    } catch (e) {}
    return null;
  }

  /* Would the original take one of the two typed paths? */
  function typedSource(gc, section) {
    if (section && isArr(section.renderedPanels) && section.renderedPanels.length) return 'rendered';
    try {
      var key = sectionKey(gc, section);
      if (key === null || typeof gc.calculateJobCutPlan !== 'function') return null;
      var plan = gc.calculateJobCutPlan()[key];
      if (plan && plan.valid && plan.panels && plan.panels.length) return 'cutplan';
    } catch (e) {}
    return null;
  }

  function install(gc) {
    var proto = Object.getPrototypeOf(gc);
    if (!proto || typeof proto.calculatePanelCosts !== 'function') return false;
    if (proto.__cfGatePanelWrapped) return true;
    var original = proto.calculatePanelCosts;

    proto.calculatePanelCosts = function (section, calculation) {
      var gateLen = 0, restore = false, planCache = null, origPlan = null;
      try {
        gateLen = parseInt(section && section.gateLength, 10) || 0;
        if (gateLen > 0 && typedSource(this, section)) {
          /* compute the plan with the TRUE gate length, then freeze it */
          if (typeof this.calculateJobCutPlan === 'function') {
            planCache = this.calculateJobCutPlan();
            origPlan = this.calculateJobCutPlan;
            this.calculateJobCutPlan = function () { return planCache; };
          }
          section.gateLength = SENTINEL;
          restore = true;
        }
      } catch (e) {
        restore = false;
      }

      try {
        return original.apply(this, arguments);
      } finally {
        if (restore) {
          try { section.gateLength = gateLen; } catch (e) {}
          try { if (origPlan) this.calculateJobCutPlan = origPlan; } catch (e) {}
        }
      }
    };

    proto.__cfGatePanelWrapped = 1;
    try { console.log(LOG, 'installed'); } catch (e) {}
    return true;
  }

  var tries = 0;
  (function wait() {
    var gc = window.glassCalculator || window.glassFencingCalculator;
    if (gc && install(gc)) return;
    if (++tries > 200) return;
    setTimeout(wait, 100);
  })();
})();
